import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import { dispatchMetaTransaction } from '@/lib/zy-tiptap-utils';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent } from '@tiptap/react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useMemo } from 'react';

const ADD_SOLUTION_CLASS =
    'group flex items-center justify-center gap-2 rounded-lg border-2 border-gray-300 px-4 py-1 text-gray-500 transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:border-gray-600 dark:text-gray-400 dark:hover:border-blue-500 dark:hover:bg-blue-950/20 dark:hover:text-blue-400 dark:focus:ring-offset-gray-900';

export function ZyProblemNodeView(props: ReactNodeViewProps) {
    const { node, getPos, editor, deleteNode, selected, decorations } = props;
    const isEditorReadonly = useIsEditorReadonly(editor);

    const { nodeIndex: zyProblemIndex, totalNodes: totalZyProblems } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'zyProblem',
    });

    const hasSolution = useMemo(() => {
        let foundSolution = false;
        node.forEach(child => {
            if (child.type.name === 'zyProblemSolution') {
                foundSolution = true;
            }
        });
        return foundSolution;
    }, [node]);

    const handleMoveUp = () => {
        if (zyProblemIndex === 0) {
            return;
        }
        const pos = getPos();
        if (typeof pos !== 'number') {
            return;
        }
        moveNode(editor, 'up', { pos, node });
        setTimeout(() => {
            editor.commands.focus(pos + 1, { scrollIntoView: true });
        }, 0);
        dispatchMetaTransaction(editor, { zyExerciseUpdate: true });
    };

    const handleMoveDown = () => {
        const pos = getPos();
        if (typeof pos !== 'number') {
            return;
        }
        moveNode(editor, 'down', { pos, node });
        dispatchMetaTransaction(editor, { zyExerciseUpdate: true });
    };

    const handleAddSolution = () => {
        const pos = getPos();
        if (typeof pos !== 'number') {
            return;
        }
        editor.commands.insertZyProblemSolution(node, pos);
    };

    const handleDeleteNode = () => {
        if (totalZyProblems <= 1) {
            return;
        }
        if (window.confirm('Are you sure you want to delete this problem?')) {
            deleteNode();
            dispatchMetaTransaction(editor, { zyExerciseUpdate: true });
        }
    };

    const isDeleteDisabled = () => totalZyProblems <= 1;
    const isMoveUpDisabled = () => zyProblemIndex === 0 || totalZyProblems <= 1;
    const isMoveDownDisabled = () => zyProblemIndex === totalZyProblems - 1 || totalZyProblems <= 1;

    const getMoveTooltip = () => {
        if (totalZyProblems <= 1) return 'Only one problem in the exercise';
        if (zyProblemIndex === 0) return 'Problem is already at the top of the exercise';
        if (zyProblemIndex === totalZyProblems - 1) return 'Problem is already at the bottom of the exercise';
        return '';
    };

    const label = zyProblemIndex !== -1 ? `(${String.fromCharCode(97 + zyProblemIndex)})` : '';

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={label}
            selected={selected}
            containerClassName="rounded-lg border-2 border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
            contentClassName="pt-3 px-1"
            footer={
                !hasSolution ? (
                    <div className="mb-2 flex gap-2 justify-end">
                        <button
                            onClick={handleAddSolution}
                            disabled={isEditorReadonly}
                            className={ADD_SOLUTION_CLASS}
                        >
                            <PlusIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">Add Solution</span>
                        </button>
                    </div>
                ) : undefined
            }
        >
            <div className="flex gap-1">
                <div contentEditable={false}>
                    <div className="flex flex-col gap-2">
                        <Button
                            onClick={handleMoveUp}
                            data-style="ghost"
                            aria-label="Move problem up"
                            disabled={isMoveUpDisabled() || isEditorReadonly}
                            tooltip={isMoveUpDisabled() ? getMoveTooltip() : 'Move problem up'}
                        >
                            <AlignTopIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            data-style="ghost"
                            aria-label="Move problem down"
                            disabled={isMoveDownDisabled() || isEditorReadonly}
                            tooltip={isMoveDownDisabled() ? getMoveTooltip() : 'Move problem down'}
                        >
                            <AlignBottomIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                <NodeViewContent className="flex-1" />
                <Button
                    onClick={handleDeleteNode}
                    data-style="ghost"
                    aria-label="Delete problem"
                    disabled={isDeleteDisabled() || isEditorReadonly}
                    tooltip={isDeleteDisabled() ? 'An exercise must have at least one problem' : 'Delete problem'}
                >
                    <Trash2Icon className="h-4 w-4" />
                </Button>
            </div>
        </CommonNodeViewWrapper>
    );
}
